<template>
  <page-header title="Login attempts">
    <div v-if="loginAttemptsStatistic" class="flex items-center gap-x-4">
      <button
        class="btn-primary ml-auto h-10"
        :disabled="loginAttemptsStatistic.total === 0"
        @click="onDeleteAttempts('expired-blocks')"
      >
        <TrashIcon class="size-5 -ml-1 mr-2" />
        Delete expired attempts
      </button>
      <button
        class="btn-primary ml-auto h-10"
        :disabled="loginAttemptsStatistic.total === 0"
        @click="onDeleteAttempts('old-successful')"
      >
        <TrashIcon class="size-5 -ml-1 mr-2" />
        Delete old successful attempts
      </button>
      <button
        class="btn-primary ml-auto h-10"
        :disabled="loginAttemptsStatistic.total === 0"
        @click="onDeleteAttempts('old-failed')"
      >
        <TrashIcon class="size-5 -ml-1 mr-2" />
        Delete old failed attempts
      </button>
    </div>
  </page-header>

  <grid-items-empty v-if="isEmpty && search === null" />

  <template v-else>
    <!-- Stats -->
    <div v-if="loginAttemptsStatistic" class="bg-gradient-to-br from-white to-gray-50 shadow-lg rounded-xl p-6 mb-8 border border-gray-100">
      <div class="flex items-center mb-4">
        <h2 class="text-2xl font-bold text-gray-800">
          Login Statistics
        </h2>
      </div>

      <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
        <!-- Total -->
        <div class="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">
                Total
              </p>
              <p class="text-2xl font-bold text-gray-900">
                {{ loginAttemptsStatistic.total.toLocaleString() }}
              </p>
            </div>
            <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Successful -->
        <div class="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">
                Successful
              </p>
              <p class="text-2xl font-bold text-green-600">
                {{ loginAttemptsStatistic.successful.toLocaleString() }}
              </p>
            </div>
            <div class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Failed -->
        <div class="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">
                Failed
              </p>
              <p class="text-2xl font-bold text-red-600">
                {{ loginAttemptsStatistic.failed.toLocaleString() }}
              </p>
            </div>
            <div class="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Blocked -->
        <div class="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">
                Blocked
              </p>
              <p class="text-2xl font-bold text-orange-600">
                {{ loginAttemptsStatistic.blocked.toLocaleString() }}
              </p>
            </div>
            <div class="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m0 0v2m0-2h2m-2 0H10m4-6V9a2 2 0 10-4 0v2m4 0a1 1 0 011 1v4a1 1 0 01-1 1H9a1 1 0 01-1-1v-4a1 1 0 011-1h6z" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Success Rate -->
        <div class="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">
                Success Rate
              </p>
              <p class="text-2xl font-bold text-indigo-600">
                {{ loginAttemptsStatistic.successRate }}%
              </p>
            </div>
            <div class="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Success Rate Bar -->
      <div class="mt-6">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium text-gray-700">Overall Success Rate</span>
          <span class="text-sm text-gray-500">{{ loginAttemptsStatistic.successful }} / {{ loginAttemptsStatistic.total }}</span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-3">
          <div
            class="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500 ease-out"
            :style="{ width: `${Math.min(100, parseFloat(loginAttemptsStatistic.successRate))}%` }"
          />
        </div>
      </div>
    </div>
    <!-- Stats -->
    <div class="flex justify-between mb-4">
      <input-search v-model.trim="search" class="max-w-lg grow" />

      <reload-button class="ml-4" @click="reloadGrid" />
    </div>

    <grid
      ref="gridRef"
      :data-handler="getLoginAttempts"
      :columns="columns"
      :row-class="rowClass"
      :per-page="15"
    />
  </template>
</template>

<script setup>
import { ref, markRaw } from 'vue';
import PageHeader from '../components/PageHeader.vue';
import ReloadButton from '../components/grid/ReloadButton.vue';
import InputSearch from '../components/grid/InputSearch.vue';
import GridItemsEmpty from '../components/grid/GridItemsEmpty.vue';
import Grid from '../components/grid/Grid.vue';
import IsSuccess from '../components/IsSuccess.vue';
import { TrashIcon } from '@heroicons/vue/24/outline';
import { useFetch, watchDebounced } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { useRouter } from 'vue-router';
import { apiDomain } from '../composables/useConstants';
import { useToast } from 'vue-toastification';
import { Modal } from '../composables/useModal';
import { formatDate } from '../utils/formatters';

const gridRef = ref(null);
const isEmpty = ref(false);
const router = useRouter();
const loginAttemptsStatistic = ref(null);

const toast = useToast();

function rowClass(_row, index) {
  if (_row.tier === 'admin') {
    return 'bg-primary-100/50';
  }
  if (_row.tier === 'seo') {
    return 'bg-fuchsia-100/50';
  }
  if (_row.tier === 'staff') {
    return 'bg-sky-100/50';
  }
  return (index % 2 ? 'bg-zinc-200/10' : '') + (_row.deletedAt ? ' [&>td:not(:last-child)]:!opacity-30' : ''); // stripes class for row
}

function reloadGrid(updateQuery = {}) {
  if (gridRef.value?.reload) {
    gridRef.value.reload(updateQuery);
  }
}

const search = useRouteQuery('search', null);

watchDebounced(
  search,
  () => {
    reloadGrid({ offset: 0 });
  },
  { debounce: 500, maxWait: 1000 }
);

function getUrl({ limit, offset, sort, direction }) {
  const data = {
    limit,
    offset,
    order: sort,
    direction: direction?.toLowerCase(),
    search: search.value ?? ''
  };

  // Filter empty values
  const filteredData = Object.fromEntries(
    Object.entries(data).filter(([ , value ]) => value !== '' && value != null)
  );

  const searchParams = new URLSearchParams(filteredData);

  return `${apiDomain}/api/login-attempts/?${searchParams}`;
}

async function getLoginAttempts(args) {
  const { limit, offset, sort, direction } = args;
  const { data } = await useFetch(getUrl({ limit, offset, sort, direction })).get().json();

  isEmpty.value = !data.value.total;

  loginAttemptsStatistic.value = data.value.stats;
  return {
    total: data.value.total,
    rows: data.value.attempts
  };
}

const columns = [
  {
    title: '#',
    prop: 'id',
    class: 'cursor-pointer text-xs font-mono',
    sortable: true,
    click: row => router.push(`/user-login-attempts/${encodeURIComponent(row.email)}`)
  },
  {
    title: 'Email',
    prop: 'email',
    class: 'cursor-pointer !py-0',
    click: row => router.push(`/user-login-attempts/${encodeURIComponent(row.email)}`),
    sortable: true
  },
  {
    title: 'IP address',
    prop: 'ipAddress',
    class: 'cursor-pointer !py-0',
    click: row => router.push(`/user-login-attempts/${encodeURIComponent(row.email)}`)
  },
  {
    title: 'Success',
    prop: 'isSuccess',
    class: 'cursor-pointer !py-0',
    component: markRaw(IsSuccess),
    click: row => router.push(`/user-login-attempts/${encodeURIComponent(row.email)}`)
  },
  {
    title: 'Created',
    prop: 'createdAt',
    value: row => formatDate(row.createdAt),
    sortable: true
  }
];

async function onDeleteAttempts(operation) {
  const needsInput = operation === 'old-successful' || operation === 'old-failed';

  const result = await Modal.confirm({
    title: operation === 'expired-blocks'
      ? 'Delete Expired Attempts'
      : operation === 'old-successful'
        ? 'Delete Old Successful Attempts'
        : 'Delete Old Failed Attempts',
    body: operation === 'expired-blocks'
      ? 'This will delete all expired blocked attempts. This action cannot be undone. Continue?'
      : operation === 'old-successful'
        ? 'This will delete successful login attempts older than the specified number of days. This action cannot be undone.'
        : 'This will delete failed login attempts (non-blocked) older than the specified number of days. This action cannot be undone.',
    okButton: operation === 'expired-blocks'
      ? 'Delete Expired'
      : operation === 'old-successful'
        ? 'Delete Old Successful'
        : 'Delete Old Failed',
    iconColor: 'danger',
    inputField: needsInput ? {
      id: 'olderThanDays',
      label: 'Delete attempts older than (days):',
      type: 'number',
      placeholder: operation === 'old-failed' ? '7' : '30',
      defaultValue: operation === 'old-failed' ? '7' : '30',
      min: '0',
      max: '365',
      description: `Enter the number of days (0-365). ${operation === 'old-failed' ? 'Failed attempts' : 'Successful attempts'} older than this will be deleted. Use 0 to delete ALL ${operation === 'old-failed' ? 'failed' : 'successful'} attempts.`
    } : null
  });

  // Check the result depending on whether there is an input
  const confirmed = needsInput ? result.confirmed : result;

  if (!confirmed) {
    return;
  }

  const olderThanDays = needsInput ? (result.inputValue === '' ? (operation === 'old-failed' ? 7 : 30) : parseInt(result.inputValue)) : undefined;

  try {
    const payload = { operation: operation };
    if (needsInput) {
      payload.olderThanDays = olderThanDays;
    }

    const { data } = await useFetch(`${apiDomain}/api/login-attempts/cleanup`)
      .post(payload)
      .json();

    if (data.value?.success) {
      let message;
      if (needsInput) {
        if (olderThanDays === 0) {
          message = `Successfully deleted ALL ${data.value.clearedCount} ${operation === 'old-successful' ? 'successful' : 'failed'} login attempts`;
        } else {
          message = `Successfully deleted ${data.value.clearedCount} ${operation === 'old-successful' ? 'successful' : 'failed'} login attempts older than ${olderThanDays} days`;
        }
      } else {
        message = data.value.message || 'Successfully deleted expired attempts';
      }
      toast.success(message);
      reloadGrid();
      return;
    }

    const errorMessage = operation === 'expired-blocks'
      ? 'Error deleting expired attempts'
      : operation === 'old-successful'
        ? 'Error deleting old successful attempts'
        : 'Error deleting old failed attempts';
    toast.error(errorMessage);
  } catch (error) {
    console.error('Cleanup error:', error);
    const errorMessage = operation === 'expired-blocks'
      ? 'Error deleting expired attempts'
      : operation === 'old-successful'
        ? 'Error deleting old successful attempts'
        : 'Error deleting old failed attempts';
    toast.error(errorMessage);
  }
}
</script>
